import React, { useState, useEffect, useRef } from 'react';
import { 
  Calendar, 
  User, 
  FileText, 
  UserCheck, 
  Save,
  Search,
  ChevronDown,
  Check,
  X
} from 'lucide-react';

const DateInputWithLeftIcon = ({ value, onChange, min, disabled = false }) => {
  const inputRef = useRef(null);

  const handleIconClick = () => {
    if (disabled) return;
    
    if (inputRef.current) {
      if (inputRef.current.showPicker) {
        inputRef.current.showPicker(); // Modern browsers
      } else {
        inputRef.current.focus(); // Fallback
      }
    }
  };

  return (
    <div className="relative">
      <Calendar
        onClick={handleIconClick}
        className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${
          disabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-600 cursor-pointer'
        }`}
      />
      <input
        type="date"
        ref={inputRef}
        value={value}
        onChange={onChange}
        min={min}
        disabled={disabled}
        className={`w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : ''
        }`}
        style={{
          WebkitAppearance: 'none',
          MozAppearance: 'textfield',
        }}
      />
      <style jsx>{`
        input[type="date"]::-webkit-calendar-picker-indicator {
          display: none;
        }
      `}</style>
    </div>
  );
};

// Custom Alert Component
const CustomAlert = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center ${
      type === 'success' ? 'bg-green-100 border border-green-400 text-green-700' : 'bg-red-100 border border-red-400 text-red-700'
    }`}>
      {type === 'success' ? (
        <Check className="w-5 h-5 mr-2" />
      ) : (
        <X className="w-5 h-5 mr-2" />
      )}
      <span>{message}</span>
      <button onClick={onClose} className="ml-4">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

const ExaminationProcess = ({ onBack }) => {
  const [patients, setPatients] = useState([
    { 
      id: 1, 
      patientCode: 'PT001', 
      firstName: 'Rajesh', 
      lastName: 'Kumar', 
      age: 35, 
      gender: 'Male',
      tests: [
        { id: 1, name: 'Complete Blood Count (CBC)', status: 'pending' },
        { id: 2, name: 'Blood Glucose Fasting', status: 'pending' }
      ]
    },
    { 
      id: 2, 
      patientCode: 'PT002', 
      firstName: 'Priya', 
      lastName: 'Sharma', 
      age: 28, 
      gender: 'Female',
      tests: [
        { id: 3, name: 'Thyroid Profile', status: 'pending' }
      ]
    },
    { 
      id: 3, 
      patientCode: 'PT003', 
      firstName: 'Amit', 
      lastName: 'Verma', 
      age: 45, 
      gender: 'Male',
      tests: [
        { id: 4, name: 'Lipid Profile', status: 'pending' },
        { id: 5, name: 'Liver Function Test', status: 'pending' }
      ]
    },
    { 
      id: 4, 
      patientCode: 'PT004', 
      firstName: 'Sneha', 
      lastName: 'Pate;', 
      age: 36, 
      gender: 'Female',
      tests: [
        { id: 4, name: 'Lipid Profile', status: 'pending' },
        { id: 5, name: 'Liver Function Test', status: 'pending' }
      ]
    },
    { 
      id: 5, 
      patientCode: 'PT005', 
      firstName: 'Vikram', 
      lastName: 'Singh', 
      age: 35, 
      gender: 'Male',
      tests: [
        { id: 1, name: 'Complete Blood Count (CBC)', status: 'pending' },
        { id: 2, name: 'Blood Glucose Fasting', status: 'pending' },
        { id: 4, name: 'Lipid Profile', status: 'pending' }
      ]
    }
    
  ]);

  const [formData, setFormData] = useState({
    recordDate: new Date().toISOString().split('T')[0],
    selectedPatient: null,
    tests: [],
    examOperator: 'Dr. John Smith',
    examSupervisor: ''
  });

  const [patientSearch, setPatientSearch] = useState('');
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [filteredPatients, setFilteredPatients] = useState(patients);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'success' });
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (patientSearch) {
      const filtered = patients.filter(patient => 
        patient.firstName.toLowerCase().includes(patientSearch.toLowerCase()) ||
        patient.lastName.toLowerCase().includes(patientSearch.toLowerCase()) ||
        patient.patientCode.toLowerCase().includes(patientSearch.toLowerCase())
      );
      setFilteredPatients(filtered);
    } else {
      setFilteredPatients(patients);
    }
  }, [patientSearch, patients]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowPatientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelectPatient = (patient) => {
    setFormData({
      ...formData,
      selectedPatient: patient,
      tests: patient.tests.map(test => ({ 
        ...test, 
        completed: false,
        testDate: formData.recordDate
      }))
    });
    setShowPatientDropdown(false);
    setPatientSearch(`${patient.patientCode} - ${patient.firstName} ${patient.lastName}`);
  };

  const clearSelectedPatient = () => {
    setFormData({
      ...formData,
      selectedPatient: null,
      tests: []
    });
    setPatientSearch('');
  };

  const handleTestToggle = (testId) => {
    setFormData(prev => ({
      ...prev,
      tests: prev.tests.map(test => 
        test.id === testId ? { ...test, completed: !test.completed } : test
      )
    }));
  };

  const handleTestDateChange = (testId, date) => {
    setFormData(prev => ({
      ...prev,
      tests: prev.tests.map(test => 
        test.id === testId ? { ...test, testDate: date } : test
      )
    }));
  };

  const resetForm = () => {
    setFormData({
      recordDate: new Date().toISOString().split('T')[0],
      selectedPatient: null,
      tests: [],
      examOperator: 'Dr. John Smith',
      examSupervisor: ''
    });
    setPatientSearch('');
  };

  const handleSave = () => {
    if (!formData.selectedPatient) {
      setAlert({ show: true, message: 'Please select a patient', type: 'error' });
      return;
    }
    if (!formData.examSupervisor) {
      setAlert({ show: true, message: 'Please enter exam supervisor name', type: 'error' });
      return;
    }
    const testsWithoutDates = formData.tests.filter(test => !test.testDate);
    if (testsWithoutDates.length > 0) {
      setAlert({ show: true, message: 'Please select dates for all tests', type: 'error' });
      return;
    }
    
    console.log('Saving examination process:', formData);
    
    // Show success alert
    setAlert({ show: true, message: 'Examination process saved successfully!', type: 'success' });
    
    // Reset form after successful save
    setTimeout(() => {
      resetForm();
    }, 500);
  };

  return (
    <div className="bg-white rounded-lg shadow-md">
      {/* Alert */}
      {alert.show && (
        <CustomAlert 
          message={alert.message} 
          type={alert.type} 
          onClose={() => setAlert({ show: false, message: '', type: 'success' })} 
        />
      )}
      
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
            </button>
            <div>
              <h1 className="text-2xl font-bold">Examination Process</h1>
              <p className="text-blue-100">Record examination details and progress</p>
            </div>
          </div>
          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
            <FileText className="w-6 h-6" />
          </div>
        </div>
      </div>
      {/* Form Content */}
      <div className="p-6 space-y-6">
        {/* Record Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Record Date <span className="text-red-500">*</span>
          </label>
          <DateInputWithLeftIcon
            value={formData.recordDate}
            onChange={(e) => setFormData({...formData, recordDate: e.target.value})}
            min={null}
          />
        </div>
        {/* Patient Selection */}
        <div className="relative" ref={dropdownRef}>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Patient <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={patientSearch}
              onChange={(e) => {
                setPatientSearch(e.target.value);
                setShowPatientDropdown(true);
              }}
              onFocus={() => setShowPatientDropdown(true)}
              placeholder="Search patient by name or code..."
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {formData.selectedPatient ? (
              <X 
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 cursor-pointer hover:text-gray-600"
                onClick={clearSelectedPatient}
              />
            ) : (
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            )}
          </div>
          {showPatientDropdown && filteredPatients.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {filteredPatients.map(patient => (
                <div
                  key={patient.id}
                  onClick={() => handleSelectPatient(patient)}
                  className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                >
                  <div className="font-medium">{patient.firstName} {patient.lastName}</div>
                  <div className="text-sm text-gray-600">
                    {patient.patientCode} • {patient.age}y • {patient.gender}
                  </div>
                  <div className="text-xs text-gray-500">
                    Tests: {patient.tests.map(t => t.name).join(', ')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Selected Patient Details */}
        {formData.selectedPatient && (
          <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
            <h3 className="font-semibold text-blue-800 mb-3 flex items-center justify-between">
              <div className="flex items-center">
                <User className="w-5 h-5 mr-2" />
                Selected Patient
              </div>
              <button
                onClick={clearSelectedPatient}
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                Change Patient
              </button>
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-600">Name</div>
                <div className="font-medium">
                  {formData.selectedPatient.firstName} {formData.selectedPatient.lastName}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Patient Code</div>
                <div className="font-medium">{formData.selectedPatient.patientCode}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Age & Gender</div>
                <div className="font-medium">
                  {formData.selectedPatient.age}y, {formData.selectedPatient.gender}
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Tests List with Checkboxes and Dates */}
        {formData.selectedPatient && formData.tests.length > 0 && (
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
              <FileText className="w-5 h-5 mr-2" />
              Tests to be Completed
            </h3>
            <div className="space-y-4">
              {formData.tests.map(test => (
                <div key={test.id} className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                  <div className="flex items-center mb-2">
                    <input
                      type="checkbox"
                      checked={test.completed}
                      onChange={() => handleTestToggle(test.id)}
                      className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className={`ml-3 text-sm flex-1 ${test.completed ? 'line-through text-gray-500' : 'text-gray-700'}`}>
                      {test.name}
                    </span>
                    {test.completed && (
                      <Check className="w-4 h-4 text-green-500" />
                    )}
                  </div>

                  <div className="ml-7 mt-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Test Date <span className="text-red-500">*</span>
                    </label>
                    <DateInputWithLeftIcon
                      value={test.testDate || ''}
                      onChange={(e) => handleTestDateChange(test.id, e.target.value)}
                      min={formData.recordDate}
                      disabled={test.completed}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Exam Operator (Auto-filled) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Exam Operator
          </label>
          <div className="relative">
            <UserCheck className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={formData.examOperator}
              readOnly
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">Automatically filled with logged-in user</p>
        </div>
        {/* Exam Supervisor */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Exam Supervisor <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <UserCheck className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={formData.examSupervisor}
              onChange={(e) => setFormData({...formData, examSupervisor: e.target.value})}
              placeholder="Enter supervisor name..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>
        </div>
        {/* Save Button */}
        <div className="flex justify-end pt-4">
          <button
            onClick={handleSave}
            className="bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-3 rounded-lg font-medium hover:from-green-700 hover:to-green-800 transition-all duration-200 flex items-center space-x-2 shadow-lg"
          >
            <Save className="w-5 h-5" />
            <span>Save Examination Process</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExaminationProcess;